<template>
    <div
        :id="id"
        class="lni-c-child-labor-complaint"
        :class="classString"
    >
        <div class="lni-u-mb3 lni-u-pb3 lni-u-bb lni-u-b--subtle">
            <div class="lni-u-reading-width--max">
                <h1 class="lni-u-heading--1">
                    {{ text.confirmationHeading }}
                </h1>
                <p class="lni-u-heading--2 lni-u-mt3 lni-u-mb2">
                    {{ text.complaintReceived }}
                </p>
                <button
                    class="lni-u-button-reset lni-u-link-style"
                    href="#"
                    @click="print"
                >
                    <span
                        class="lnicon--print lni-u-mr1"
                        aria-hidden="true"
                    />
                    <span>{{ text.getPdf }}</span>
                </button>
                <div class="lni-u-mt2">
                    <span class="lni-u-heading--5 lni-u-text--all-caps">
                        {{ text.complaintNumber }}: {{ complaintNumber }}
                    </span>
                    <p class="lni-u-mt1">
                        {{ text.complaintNumberDisclaimer }}
                    </p>
                </div>
            </div>
        </div>
        <div class="lni-u-reading-width--max">
            <h2 class="lni-u-heading--3">
                {{ text.complaintHeading }}
            </h2>
            <div class="lni-u-mt1">
                <span class="lni-u-type--bold">
                    {{ text.complaintLabel }}:
                </span>
                <ul class="lni-u-pl3 lni-u-mb2">
                    <li
                        v-for="violation in violations"
                        :key="violation"
                    >
                        {{ violation }}
                    </li>
                </ul>
            </div>
            <div class="lni-u-mb2">
                <span class="lni-u-type--bold">
                    {{ text.descriptionLabel }}:
                </span>
                <p>
                    {{ formData.complaint.description }}
                </p>
            </div>
            <div class="lni-u-mb2 lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.dateRange }}:
                </span>
                <lni-html-element
                    :id="`${id}_date-range`"
                    :htmlString="displayDateRange(formData.complaint.startDate, formData.complaint.endDate)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.complainantLabel }}:
                </span>
                <p>
                    {{ formData.complaint.complainant }}
                </p>
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.quantityLabel }}:
                </span>
                <lni-html-element
                    :id="`${id}_quantity`"
                    :htmlString="displayText(formData.complaint.quantity)"
                />
            </div>
            <div class="lni-u-mb2 lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.relationshipLabel }}:
                </span>
                <p>
                    {{ formData.complaint.relationship }}
                </p>
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.minorFirstName }}:
                </span>
                <lni-html-element
                    :id="`${id}_minor-first-name`"
                    :htmlString="displayText(formData.complaint.firstName)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.minorLastName }}:
                </span>
                <lni-html-element
                    :id="`${id}_minor-last-name`"
                    :htmlString="displayText(formData.complaint.lastName)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.workStart }}:
                </span>
                <lni-html-element
                    :id="`${id}_work-start`"
                    :htmlString="displayText(formData.complaint.workStart)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.workEnd }}:
                </span>
                <lni-html-element
                    :id="`${id}_work-end`"
                    :htmlString="displayText(formData.complaint.workEnd)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.terminationReason }}:
                </span>
                <lni-html-element
                    :id="`${id}_end-reason`"
                    :htmlString="displayText(endReason)"
                />
            </div>
            <div
                v-if="formData.complaint.otherReason.length"
                class="lni-u-flex lni-u-flex-wrap"
            >
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.otherReason }}:
                </span>
                <lni-html-element
                    :id="`${id}_other-reason`"
                    :htmlString="formData.complaint.otherReason"
                />
            </div>
        </div>
        <div class="lni-u-reading-width--max lni-u-mt3">
            <h2 class="lni-u-heading--3">
                {{ text.employerInformation }}
            </h2>
            <div class="lni-u-mt1">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.companyName }}:
                </span>
                {{ formData.employer.name }}
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.companyPhone }}:
                </span>
                <lni-html-element
                    :id="`${id}_phone`"
                    :htmlString="displayPhoneNumber(formData.employer.phone, formData.employer.ext)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.companyEmail }}:
                </span>
                <lni-html-element
                    :id="`${id}_email`"
                    :htmlString="displayText(formData.employer.email)"
                />
            </div>
            <div class="lni-u-mv2">
                <span class="lni-u-type--bold">
                    {{ text.companyAddress }}:
                </span>
                <address v-if="formData.employer.address1.length">
                    {{ formData.employer.address1 }}<br>
                    <span v-if="formData.employer.address2">
                        {{ formData.employer.address2 }}<br>
                    </span>
                    {{ formData.employer.city }}, {{ formData.employer.state }} {{ formData.employer.zip }}
                </address>
                <lni-html-element
                    v-else
                    :id="`${id}_employer-address`"
                    :htmlString="getPlaceholder()"
                />
            </div>
            <div
                v-if="formData.employer.locationAddress1?.length"
                class="lni-u-mv2"
            >
                <span
                    class="lni-u-type--bold"
                >
                    {{ text.locationAddress }}:
                </span>
                <address>
                    {{ formData.employer.locationAddress1 }}<br>
                    <span v-if="formData.employer.locationAddress2">
                        {{ formData.employer.locationAddress2 }}<br>
                    </span>
                    {{ formData.employer.locationCity }}, {{ formData.employer.locationState }} {{ formData.employer.locationZip }}
                </address>
            </div>
            <h4 class="lni-u-heading--4 lni-u-mb1">
                {{ text.supervisorContact }}
            </h4>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.firstName }}:
                </span>
                <lni-html-element
                    :id="`${id}_supervisor-first-name`"
                    :htmlString="displayText(formData.employer.contactFirstName)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.lastName }}:
                </span>
                <lni-html-element
                    :id="`${id}_supervisor-last-name`"
                    :htmlString="displayText(formData.employer.contactLastName)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.phone }}:
                </span>
                <lni-html-element
                    :id="`${id}_supervisor-phone`"
                    :htmlString="displayPhoneNumber(formData.employer.contactPhone, formData.employer.contactExt)"
                />
            </div>
        </div>
        <div class="lni-u-reading-width--max lni-u-mt3">
            <h2 class="lni-u-heading--3">
                {{ text.contactInformation }}
            </h2>
            <div class="lni-u-mt1 lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.anonymousLabel }}:
                </span>
                {{ anonymous }}
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.firstName }}:
                </span>
                <lni-html-element
                    :id="`${id}_contact-first-name`"
                    :htmlString="displayText(formData.contact.firstName)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.lastName }}:
                </span>
                <lni-html-element
                    :id="`${id}_contact-last-name`"
                    :htmlString="displayText(formData.contact.lastName)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.phone }}:
                </span>
                <lni-html-element
                    :id="`${id}_contact-phone`"
                    :htmlString="displayPhoneNumber(formData.contact.phone)"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.email }}:
                </span>
                <lni-html-element
                    :id="`${id}_contact-email`"
                    :htmlString="displayText(formData.contact.email)"
                />
            </div>
            <div class="lni-u-mv2">
                <span class="lni-u-type--bold">
                    {{ text.address }}:
                </span>
                <address v-if="formData.contact.address1.length">
                    {{ formData.contact.address1 }}<br>
                    <span v-if="formData.contact.address2">
                        {{ formData.contact.address2 }}<br>
                    </span>
                    {{ formData.contact.city }}, {{ formData.contact.state }} {{ formData.contact.zip }}
                </address>
                <lni-html-element
                    v-else
                    :id="`${id}_contact-address`"
                    :htmlString="getPlaceholder()"
                />
            </div>
            <div class="lni-u-flex lni-u-flex-wrap">
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.preferredLanguage }}:
                </span>
                <lni-html-element
                    :id="`${id}_preferred-language`"
                    :htmlString="displayText(preferredLanguage)"
                />
            </div>
            <div
                v-if="formData.contact.otherLanguage.length"
                class="lni-u-flex lni-u-flex-wrap"
            >
                <span class="lni-u-type--bold lni-u-mr1">
                    {{ text.otherLanguage }}:
                </span>
                <lni-html-element
                    :id="`${id}_other-language`"
                    :htmlString="formData.contact.otherLanguage"
                />
            </div>
            <div class="lni-u-mt3">
                <h2 class="lni-u-heading--3">
                    {{ text.supportingDocuments }}
                </h2>
                <div
                    v-show="documents?.length === 0"
                    class="lni-u-mt1"
                >
                    {{ text.none }}
                </div>
                <lni-file-uploads
                    :id="`${id}_file-uploads`"
                    :allowDelete="false"
                />
            </div>
        </div>
        <div class="lni-u-pt3 lni-u-mt3 lni-u-bt lni-u-b--subtle">
            <lni-html-element
                :id="`${id}_content`"
                classString="lni-u-reading-width--max"
                :htmlString="text.confirmation"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name : 'LniChildLaborComplaintConfirmation',
    props: {
        classString: {
            type   : String,
            default: '',
        },
        text: {
            type    : Object,
            required: true,
        },
    },
    computed: {
        anonymous() {
            let result = this.text.no;
            if (this.formData.contact.isAnonymous) {
                result = this.text.yes;
            }
            return result;
        },
        documents() {
            return this.formData.documents;
        },
        endReason() {
            let result = '';
            const value = this.formData.complaint.endReason;
            if (value.length) {
                result = this.terminationOptions.find(opt => opt.value === value).text;
            }
            return result;
        },
        preferredLanguage() {
            const { otherLanguage, preferredLanguage } = this.formData.contact || {};

            if (otherLanguage) {
                return otherLanguage;
            }

            const foundOption = this.languageOptions.find(opt => opt.value === preferredLanguage);
            return foundOption?.text || preferredLanguage;
        },
        violations() {
            const data = this.formData.complaint.violations;
            return data.map(item => {
                return this.violationOptions.find(opt => opt.value === item.type).text;
            });
        },
        ...mapGetters({
            complaintNumber   : 'appState/complaintNumber',
            formData          : 'appState/formData',
            languageOptions   : 'appState/languageOptions',
            terminationOptions: 'appState/terminationOptions',
            violationOptions  : 'appState/violationOptions',
        }),
    },
    mounted() {
        if (this.documents.length) {
            this.$store.commit('setAttribute', {
                id       : `${ this.id }_file-uploads`,
                attribute: 'uploadedFiles',
                value    : this.documents,
            }, { root: true });
        }
    },
    methods: {
        displayDateRange(startDate, endDate) {
            let result = this.getPlaceholder();
            if (startDate.length && endDate.length) {
                result = `${ startDate } to ${ endDate }`;
            }

            return result;
        },
        displayPhoneNumber(phone, ext) {
            let result = this.getPlaceholder();
            if (phone.length) {
                result = phone;
                if (ext?.length) {
                    result = `${ result } ext. ${ ext }`;
                }
            }

            return result;
        },
        displayText(text) {
            return text.length ? text : this.getPlaceholder();
        },
        getPlaceholder() {
            return `<span aria-hidden="true">---</span><span class="lni-u-visually-hidden">Blank</span>`;
        },
        print() {
            window.print();
        },
    },
};
</script>

<style lang ="scss">
@import '@gov.wa.lni/framework.one-lni/source/styles/settings';

.lni-c-child-labor-complaint {
    .lni-u-heading--5 {
        color: $text--alternate;
    }
}
</style>